import ContentWorkspace from "./pages/ContentWorkspace";
import ViralPosts from "./pages/ViralPosts";
import OnboardingPage, { PublicOnboarding } from "./pages/Onboarding";
import Portal from "./pages/Portal";
import CreativeWorkspace from "./pages/CreativeWorkspace";
import ProjectsWorkspace from "./pages/ProjectsWorkspace";
import TractionWorkspace from "./pages/TractionWorkspace";
import CrmPipeline from "./pages/CrmPipeline";
import CrmContacts from "./pages/CrmContacts";
import CrmForms from "./pages/CrmForms";
import CrmBookings from "./pages/CrmBookings";
import CrmDrips from "./pages/CrmDrips";
import Spend from "./pages/Spend";
import Tracking from "./pages/Tracking";
import Keywords from "./pages/Keywords";
import WebAnalytics from "./pages/WebAnalytics";
import Websites from "./pages/Websites";
import Conversions from "./pages/Conversions";
import Standup from "./pages/Standup";
import Intelligence from "./pages/Intelligence";
import AiSearch from "./pages/AiSearch";
import SeoPlan from "./pages/SeoPlan";
import SeoWriter from "./pages/SeoWriter";
import ClientDashboard from "./pages/ClientDashboard";
import Reports from "./pages/Reports";
import { ClientProvider } from "./client-context";
import Training from "./pages/Training";
import { SopsPage } from "./pages/Sops";
import TierGate from "./components/TierGate";
import ClientSettings from "./pages/ClientSettings";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, type ReactNode } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { useAuth } from "./auth";
import { safePortalReturn } from "./auth-routing";
import Layout from "./components/Layout";
import { ErrorState } from "./components/Ui";
import BootSplash from "./components/BootSplash";
import { settleBootSplash } from "./boot";
import Login from "./pages/Login";
import PasswordRecovery from "./pages/PasswordRecovery";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Tasks from "./pages/Tasks";
import Health from "./pages/Health";
import Ads from "./pages/Ads";
import AdsClient from "./pages/AdsClient";
import AdsReview from "./pages/AdsReview";
import AdsCampaigns from "./pages/AdsCampaigns";
import FacebookAds from "./pages/FacebookAds";
import FacebookPage from "./pages/FacebookPage";
import SocialPublishing from "./pages/SocialPublishing";
import Competitors from "./pages/Competitors";
import AdsAi from "./pages/AdsAi";
import Jarvis from "./pages/Jarvis";
import Compare from "./pages/Compare";
import AnalystPage from "./pages/Analyst";
import ApprovalsPage from "./pages/Approvals";
import SettingsPage from "./pages/Settings";
import {
  ReputationPage,
} from "./pages/Modules";
import Team from "./pages/Team";
import KnowledgeBase from "./pages/KnowledgeBase";
import PromptLibrary from "./pages/PromptLibrary";
import AgencyProjects from "./pages/AgencyProjects";
import ContentReview from "./pages/ContentReview";
import Pages from "./pages/Pages";
import PageDomains from "./pages/PageDomains";
import PageEditor from "./landing/editor/PageEditor";
import PageEditorHarness from "./landing/editor/PageEditorHarness";
import TemplatePreviewHarness from "./landing/preview/TemplatePreviewHarness";

/** /assets was the Creative hub under a second name; keep the client in the URL. */
function RedirectKeepingSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

/** One page failing must not take the sidebar with it; a new route clears the error. */
function RouteBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

/** The staff shell around every page but the page editor: sidebar, top bar, section tabs. */
function StaffShell() {
  return (
    <Layout>
      <RouteBoundary>
        <Outlet />
      </RouteBoundary>
    </Layout>
  );
}

/**
 * Screens for screenshot QA in development only, with no sign-in: the page
 * editor on stub data and one starter template rendered like a live page.
 * Never in a production build (import.meta.env.DEV is false there).
 */
const DEV_SCREENS: { path: string; Screen: () => ReactNode }[] = import.meta.env.DEV
  ? [
      { path: "/dev/page-editor-harness", Screen: PageEditorHarness },
      { path: "/dev/template-preview", Screen: TemplatePreviewHarness },
    ]
  : [];
const devRoutes = () => DEV_SCREENS.map(({ path, Screen }) => <Route key={path} path={path} element={<Screen />} />);

function ProtectedApp() {
  const { user, loading, sessionError, retrySession } = useAuth();
  if (sessionError)
    return <div className="full-state"><ErrorState message={sessionError} onRetry={retrySession} /></div>;
  if (loading) return <BootSplash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "client") return <Navigate to="/portal" replace />;
  return (
    <ClientProvider>
      <Routes>
        {/* The page editor fills the screen (its own top bar has Back to pages). */}
        <Route path="/pages/:id" element={<RouteBoundary><PageEditor /></RouteBoundary>} />
        <Route element={<StaffShell />}>
          <Route path="/dashboard" element={<ClientDashboard />} />
          <Route path="/ad-spend" element={<TierGate path="/ad-spend" title="Ad spend"><Spend /></TierGate>} />
          <Route path="/tracking" element={<Tracking />} />
          <Route path="/keywords" element={<TierGate path="/keywords" title="Keywords"><Keywords /></TierGate>} />
          <Route path="/seo-plan" element={<TierGate path="/seo-plan" title="SEO plan"><SeoPlan /></TierGate>} />
          <Route path="/seo-writer" element={<TierGate path="/seo-writer" title="SEO writer"><SeoWriter /></TierGate>} />
          <Route path="/ai-search" element={<TierGate path="/ai-search" title="AI search"><AiSearch /></TierGate>} />
          <Route path="/website" element={<TierGate path="/website" title="Website analytics"><WebAnalytics /></TierGate>} />
          <Route path="/standup" element={<Standup />} />
          <Route path="/call-intelligence" element={<Intelligence />} />
          <Route path="/" element={<Dashboard />} />
          <Route path="/jarvis" element={<Jarvis />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/clients/:id" element={<ClientDetail />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/ads" element={<Ads />} />
          <Route path="/ads/:id" element={<TierGate path="/ads" title="Ads report"><AdsClient /></TierGate>} />
          <Route path="/ads-review" element={<TierGate path="/ads-review" title="Weekly review"><AdsReview /></TierGate>} />
          <Route path="/ads-campaigns" element={<TierGate path="/ads-campaigns" title="Campaigns"><AdsCampaigns /></TierGate>} />
          <Route path="/ads-ai" element={<TierGate path="/ads-ai" title="Ads AI"><AdsAi /></TierGate>} />
          <Route path="/facebook-ads" element={<TierGate path="/facebook-ads" title="Facebook ads"><FacebookAds /></TierGate>} />
          <Route path="/facebook-page" element={<TierGate path="/facebook-page" title="Facebook page"><FacebookPage /></TierGate>} />
          <Route path="/publishing" element={<SocialPublishing />} />
          <Route path="/competitors" element={<TierGate path="/competitors" title="Competitors"><Competitors /></TierGate>} />
          <Route path="/health" element={<Health />} />
          <Route path="/websites" element={<Websites />} />
          <Route path="/conversions" element={<Conversions />} />
          <Route path="/analyst" element={<AnalystPage />} />
          <Route path="/call-center" element={<TierGate path="/call-center" title="Call center reports"><Reports /></TierGate>} />
          <Route path="/reputation" element={<TierGate path="/reputation" title="Reviews & citations"><ReputationPage /></TierGate>} />
          <Route path="/approvals" element={<ApprovalsPage />} />
          <Route path="/content-ideas" element={<TierGate path="/content-ideas" title="Content pipeline"><ContentWorkspace /></TierGate>} />
          <Route path="/viral" element={<TierGate path="/viral" title="Viral posts"><ViralPosts /></TierGate>} />
          <Route path="/content" element={<TierGate path="/content" title="Content review"><ContentReview /></TierGate>} />
          <Route path="/creative" element={<TierGate path="/creative" title="Creative hub"><CreativeWorkspace /></TierGate>} />
          <Route path="/projects" element={<TierGate path="/projects" title="Projects"><ProjectsWorkspace /></TierGate>} />
          <Route path="/agency/projects" element={<AgencyProjects />} />
          <Route path="/traction" element={<TractionWorkspace />} />
          <Route path="/crm" element={<CrmPipeline />} />
          <Route path="/crm/contacts" element={<CrmContacts />} />
          <Route path="/crm/forms" element={<CrmForms />} />
          <Route path="/crm/bookings" element={<CrmBookings />} />
          <Route path="/crm/drips" element={<CrmDrips />} />
          <Route path="/pages" element={<Pages />} />
          <Route path="/pages/domains" element={<PageDomains />} />
          <Route path="/sops" element={<SopsPage />} />
          <Route path="/training" element={<Training />} />
          <Route path="/team" element={<Team />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/client-settings" element={<ClientSettings />} />
          <Route path="/prompts" element={<PromptLibrary />} />
          <Route path="/knowledge" element={<KnowledgeBase />} />
          <Route path="/knowledge/:code" element={<KnowledgeBase />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/work-queue" element={<RedirectKeepingSearch to="/tasks" />} />
          <Route path="/calls" element={<RedirectKeepingSearch to="/call-intelligence" />} />
          <Route path="/callcenter" element={<RedirectKeepingSearch to="/call-center" />} />
          <Route path="/adspend" element={<RedirectKeepingSearch to="/ad-spend" />} />
          <Route path="/assets" element={<RedirectKeepingSearch to="/creative" />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </ClientProvider>
  );
}

export default function App() {
  const { user, loading } = useAuth();
  const location = useLocation();
  // A screen that does not wait on the session (sign-in, recovery, public forms) lets the boot splash go.
  useEffect(() => { settleBootSplash(); });
  const signedInDestination = user?.role === 'client'
    ? safePortalReturn(new URLSearchParams(location.search).get('next')) || '/portal'
    : '/';
  // Recovery and token-based access do not require a working staff session.
  // Keep transient session errors inside the protected app, where retry belongs.
  return (
    <Routes>
      <Route path="/onboarding/:token" element={<PublicOnboarding />} />
      <Route path="/forgot-password" element={<PasswordRecovery key="forgot" />} />
      <Route path="/reset-password" element={<PasswordRecovery key="reset" reset />} />
      <Route path="/client-login" element={!loading && user ? <Navigate to={signedInDestination} replace /> : <Login client />} />
      <Route path="/portal/:token" element={<Portal />} />
      <Route path="/portal" element={<Portal />} />
      <Route
        path="/login"
        element={!loading && user ? <Navigate to={signedInDestination} replace /> : <Login />}
      />
      {devRoutes()}
      <Route path="*" element={<ProtectedApp />} />
    </Routes>
  );
}
